// Unified Export Engine for AccToo
// Handles PDF, Excel, CSV, JSON exports with consistent branding

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { formatCurrency } from '../currencyUtils';
import { getCompanyInfo } from '../companyUtils';

class ExportEngine {
  constructor(options = {}) {
    this.company = options.company || null;
    this.branding = options.branding || this.getDefaultBranding();
    this.currency = options.currency || 'INR';
  }

  getDefaultBranding() {
    return {
      primaryColor: '#3B82F6',
      secondaryColor: '#10B981',
      logo: null,
      footer: 'ACCTOO - Simplifying Accounting, Together',
      fontSize: {
        header: 16,
        body: 12,
        footer: 8
      }
    };
  }

  async exportToPDF(data, config = {}) {
    const doc = new jsPDF(config.orientation || 'portrait', 'mm', config.pageSize || 'a4');

    // Add header with branding
    await this.addPDFHeader(doc, config.title);

    // Add main content
    if (config.type === 'table') {
      this.addPDFTable(doc, data, config);
    } else if (config.type === 'invoice') {
      await this.addPDFInvoice(doc, data, config);
    } else {
      this.addPDFContent(doc, data, config);
    }

    // Add footer
    this.addPDFFooter(doc);

    // Save file
    const filename = config.filename || `${config.title || 'export'}-${Date.now()}.pdf`;
    doc.save(filename);

    return { success: true, filename };
  }

  async addPDFHeader(doc, title) {
    let yPos = 15;

    // Add logo if available
    if (this.branding.logo) {
      try {
        // Add logo image (assuming base64 or URL)
        doc.addImage(this.branding.logo, 'JPEG', 15, yPos, 30, 15);
        yPos += 20;
      } catch (error) {
        console.warn('Logo loading failed:', error);
      }
    }

    // Add company name
    doc.setFontSize(this.branding.fontSize.header);
    doc.setTextColor(this.branding.primaryColor);
    doc.setFont('helvetica', 'bold');
    doc.text(this.company?.firmName || 'ACCTOO', 15, yPos);
    yPos += 8;

    // Add title
    if (title) {
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.text(title, 15, yPos);
      yPos += 10;
    }

    return yPos;
  }

  addPDFTable(doc, data, config) {
    const tableConfig = {
      startY: config.startY || 40,
      headStyles: {
        fillColor: this.hexToRgb(this.branding.primaryColor),
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: 0
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      }
    };

    doc.autoTable({
      head: [config.headers || Object.keys(data[0] || {})],
      body: data.map(row => config.columns ? config.columns.map(col => row[col]) : Object.values(row)),
      ...tableConfig
    });
  }

  async addPDFInvoice(doc, data, config) {
    let yPos = 40;

    // Invoice header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', 105, yPos, { align: 'center' });
    yPos += 15;

    // Invoice details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice No: ${data.invoiceNumber || 'N/A'}`, 15, yPos);
    doc.text(`Date: ${data.date || new Date().toLocaleDateString()}`, 140, yPos);
    yPos += 10;

    // Party details
    if (data.partyName) {
      doc.text(`To: ${data.partyName}`, 15, yPos);
      yPos += 8;
      if (data.partyAddress) {
        doc.setFontSize(10);
        doc.text(data.partyAddress, 15, yPos);
        yPos += 8;
      }
    }

    // Items table
    if (data.items && data.items.length > 0) {
      yPos += 10;
      const items = data.items.map(item => [
        item.name || item.itemName,
        item.quantity || 1,
        formatCurrency(item.rate || item.price || 0, this.currency),
        formatCurrency((item.quantity || 1) * (item.rate || item.price || 0), this.currency)
      ]);

      doc.autoTable({
        head: [['Item', 'Qty', 'Rate', 'Amount']],
        body: items,
        startY: yPos,
        headStyles: { fillColor: this.hexToRgb(this.branding.primaryColor) }
      });

      yPos = doc.lastAutoTable.finalY + 10;
    }

    // Total
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${formatCurrency(data.total || 0, this.currency)}`, 140, yPos);
  }

  addPDFContent(doc, data, config) {
    let yPos = config.startY || 40;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');

    if (typeof data === 'string') {
      const lines = doc.splitTextToSize(data, 180);
      doc.text(lines, 15, yPos);
    } else if (Array.isArray(data)) {
      data.forEach(item => {
        doc.text(String(item), 15, yPos);
        yPos += 8;
      });
    }
  }

  addPDFFooter(doc) {
    const pageCount = doc.internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(this.branding.fontSize.footer);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
      doc.text(this.branding.footer, 15, doc.internal.pageSize.height - 10);
    }
  }

  async exportToExcel(data, config = {}) {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    // Add branding to first row
    const brandRow = [
      this.company?.firmName || 'ACCTOO',
      config.title || 'Export',
      `Generated: ${new Date().toLocaleString()}`
    ];

    XLSX.utils.sheet_add_aoa(ws, [brandRow], { origin: 'A1' });
    XLSX.utils.book_append_sheet(wb, ws, config.sheetName || 'Data');

    const filename = config.filename || `${config.title || 'export'}-${Date.now()}.xlsx`;
    XLSX.writeFile(wb, filename);

    return { success: true, filename };
  }

  exportToCSV(data, config = {}) {
    const headers = config.headers || (data.length > 0 ? Object.keys(data[0]) : []);
    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers.map(header => {
          const value = row[header] || '';
          // Escape quotes and wrap in quotes if contains comma
          return value.toString().includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
        }).join(',')
      )
    ].join('\n');

    const filename = config.filename || `${config.title || 'export'}-${Date.now()}.csv`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    return { success: true, filename };
  }

  exportToJSON(data, config = {}) {
    const jsonData = {
      metadata: {
        company: this.company?.firmName || 'ACCTOO',
        title: config.title || 'Export',
        generated: new Date().toISOString(),
        totalRecords: data.length
      },
      data: data
    };

    const filename = config.filename || `${config.title || 'export'}-${Date.now()}.json`;
    const jsonString = JSON.stringify(jsonData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    return { success: true, filename };
  }

  async exportToImage(elementId, config = {}) {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error('Element not found for image export');
    }

    const canvas = await html2canvas(element, {
      scale: config.scale || 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff'
    });

    const filename = config.filename || `${config.title || 'export'}-${Date.now()}.png`;
    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL();
    link.click();

    return { success: true, filename };
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16)
    ] : [59, 130, 246]; // Default blue
  }
}

export default ExportEngine;
